import { TCompanyQuestionSessionHistoryResponse } from './fetchGetCompanyQuestionSessionHistory';

const storageKey = (applicationId: string) =>
  `company_question_session_${applicationId}`;

const isValidSession = (
  value: unknown,
): value is TCompanyQuestionSessionHistoryResponse => {
  const session = value as TCompanyQuestionSessionHistoryResponse;
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray(session.history) &&
    typeof session.session_exists === 'boolean' &&
    typeof session.completed === 'boolean'
  );
};

export const readStoredSession = (
  applicationId: string,
): TCompanyQuestionSessionHistoryResponse | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(applicationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeStoredSession = (
  applicationId: string,
  session: TCompanyQuestionSessionHistoryResponse,
) => {
  try {
    sessionStorage.setItem(storageKey(applicationId), JSON.stringify(session));
  } catch {
    // sessionStorage unavailable (e.g. private mode) — fallback is best-effort
  }
};

export const clearStoredSession = (applicationId: string) => {
  try {
    sessionStorage.removeItem(storageKey(applicationId));
  } catch {
    // best-effort
  }
};

/**
 * sessionStorage is a per-tab mirror of the session cache that survives a
 * same-tab drop-off (refresh / network blip) where Django's read-after-write
 * can lag the just-submitted POST.
 *
 * The flow is append-only — questions are added, never edited — so the longer
 * history is always the more advanced one. The server is authoritative about
 * whether a session exists at all, so a reset application (session_exists:
 * false) discards any stale stored session.
 */
export const mergeStoredSession = (
  server: TCompanyQuestionSessionHistoryResponse,
  stored: TCompanyQuestionSessionHistoryResponse | null,
): TCompanyQuestionSessionHistoryResponse => {
  if (!server.session_exists || !stored) return server;
  // Append-only flow: longer history wins (no need to diff contents).
  return stored.history.length > server.history.length ? stored : server;
};
